(() => {
  const lang = document.documentElement.lang || 'en';

  const formatDates = () => {
    document.querySelectorAll('.nt-local-date[data-date]').forEach((el) => {
      const raw = el.dataset.date;
      if (!raw) return;
      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) return;
      const style = el.dataset.dateStyle || 'day-month';
      const options = style === 'month-year'
        ? { month: 'long', year: 'numeric' }
        : { day: 'numeric', month: 'long' };
      el.textContent = new Intl.DateTimeFormat(lang, options).format(date);
    });
  };

  const setupReveals = () => {
    const items = document.querySelectorAll('[data-nt-reveal]:not([data-nt-reveal-ready])');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((el) => { el.classList.add('is-visible'); el.setAttribute('data-nt-reveal-ready', 'true'); });
      return;
    }
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    items.forEach((el) => { el.setAttribute('data-nt-reveal-ready', 'true'); observer.observe(el); });
  };

  const setupFacetForms = () => {
    document.querySelectorAll('[data-nt-facets-form]').forEach((form) => {
      if (form.dataset.ntFacetsReady === 'true') return;
      form.dataset.ntFacetsReady = 'true';
      form.addEventListener('change', () => form.submit());
    });
  };

  const setupProtectedPrintMedia = () => {
    document.querySelectorAll(
      '.nt-template-prints #MainContent .product-information__media:not([data-nt-media-protected])'
    ).forEach((media) => {
      media.setAttribute('data-nt-media-protected', 'true');
      media.querySelectorAll('img').forEach((image) => image.setAttribute('draggable', 'false'));
      media.addEventListener('contextmenu', (event) => event.preventDefault());
      media.addEventListener('dragstart', (event) => event.preventDefault());
      media.addEventListener('copy', (event) => event.preventDefault());
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    formatDates();
    setupReveals();
    setupFacetForms();
    setupProtectedPrintMedia();
  });

  document.addEventListener('shopify:section:load', () => {
    formatDates();
    setupReveals();
    setupFacetForms();
    setupProtectedPrintMedia();
  });
})();

class NtPrintVariantDetails extends HTMLElement {
  connectedCallback() {
    this.sectionRoot = this.closest('.shopify-section, dialog');
    this.sectionRoot?.addEventListener('shopify:product:select', this.handleProductSelect);
  }

  disconnectedCallback() {
    this.sectionRoot?.removeEventListener('shopify:product:select', this.handleProductSelect);
  }

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) return;

        const updatedDetails = detail.html.querySelector(
          `nt-print-variant-details[data-block-id="${this.dataset.blockId}"]`
        );
        if (!updatedDetails || !this.isConnected) return;

        this.replaceWith(updatedDetails.cloneNode(true));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[nt-print-variant-details] Variant update failed:', error);
      });
  };
}

if (!customElements.get('nt-print-variant-details')) {
  customElements.define('nt-print-variant-details', NtPrintVariantDetails);
}

class NtPrintFaqController extends HTMLElement {
  connectedCallback() {
    const variantsElement = this.querySelector('[data-nt-print-faq-variants]');

    try {
      this.variants = JSON.parse(variantsElement?.textContent || '[]');
    } catch (error) {
      this.variants = [];
      console.warn('[nt-print-faq-controller] Variant data could not be read:', error);
    }

    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const urlVariant = this.variants.find((item) => String(item.id) === String(urlVariantId));
    this.updateVisibility(urlVariant?.edition || this.dataset.currentEdition || 'all');
    document.addEventListener('shopify:product:select', this.handleProductSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.handleProductSelect);
  }

  updateVisibility(edition) {
    this.querySelectorAll('[data-nt-faq-scope]').forEach((item) => {
      const scope = item.dataset.ntFaqScope || 'all';
      const shouldShow = scope === 'all' || scope === edition;
      item.hidden = !shouldShow;
      if (!shouldShow && item instanceof HTMLDetailsElement) item.open = false;
    });

    this.querySelectorAll('[data-nt-edition-group]').forEach((group) => {
      const scope = group.dataset.ntEditionGroup;
      group.hidden = scope !== edition && !(edition === 'all' && scope === 'open');
    });

    this.querySelectorAll('[data-nt-edition-label]').forEach((label) => {
      const scope = label.dataset.ntEditionLabel;
      label.hidden = scope !== edition && !(edition === 'all' && scope === 'open');
    });

    this.dataset.currentEdition = edition;
  }

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card') || !event.promise) return;

    const requestedVariantId = event.detail?.variantId;
    const requestedVariant = this.variants.find((item) => String(item.id) === String(requestedVariantId));

    if (requestedVariant) this.updateVisibility(requestedVariant.edition || 'all');

    event.promise
      .then(({ detail, variant: selectedVariant }) => {
        if (detail?.productId && String(detail.productId) !== String(this.dataset.productId)) return;

        const variantId = detail?.resource?.id || selectedVariant?.id || requestedVariantId;
        const variantData = this.variants.find((item) => String(item.id) === String(variantId));
        if (variantData) this.updateVisibility(variantData.edition || 'all');
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[nt-print-faq-controller] Variant update failed:', error);
      });
  };
}

if (!customElements.get('nt-print-faq-controller')) {
  customElements.define('nt-print-faq-controller', NtPrintFaqController);
}
